import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import SessionDropdown from 'flarum/forum/components/SessionDropdown';
import Button from 'flarum/common/components/Button';
import FieldSet from 'flarum/common/components/FieldSet';
import ProfileModal from './components/ProfileModal';
import ProfilePage from './components/ProfilePage';
import ColorSchemePage from './components/ColorSchemePage';
import UserPage from 'flarum/forum/components/UserPage';
import LinkButton from 'flarum/common/components/LinkButton';
import Separator from 'flarum/common/components/Separator';
import Navigation from 'flarum/common/components/Navigation';
import extendSignUp, { AgreementModal } from './signup/signUp';
import { ReacceptModal } from './signup/AgreementModal';
import extendEditUserHints from '../common/editUserHints';
import HeaderSecondary from 'flarum/forum/components/HeaderSecondary';
import { override } from 'flarum/common/extend';
import extractText from 'flarum/common/utils/extractText';
import { withEye } from './components/PasswordEye';
import AvatarGalleryModal from './components/AvatarGalleryModal';
const t = (key, params) => app.translator.trans('ppk2-profile.forum.' + key, params);

/** flarum/gdpr module (only when that extension is enabled), else null. */
function gdpr(id) {
  try {
    const mod = flarum.reg.get('flarum-gdpr', id);
    return mod && mod.default || mod || null;
  } catch (e) {
    return null;
  }
}

// All user-facing text comes from app.translator.trans('ppk2-profile.forum.…').
/** /settings (core page) now opens our «Настройки» (Mithril's redirect from onmatch). */
const settingsRedirect = {
  onmatch() {
    m.route.set(app.route('ppk2.profile'), null, {
      replace: true
    });
    return new Promise(() => {});
  }
};

/**
 * Settings sub-pages (left menu of /my-profile): «‹» always leads to «Главная» of «Настройки»;
 * on /my-profile itself it leads to the forum. Same for every user.
 */
const SETTINGS_SUBPAGES = ['user.security', 'ppk2.colorScheme', 'ppk2.accountLog', 'ppk2.agreementSettings', 'ppk2.supportRequests'];
function settingsBackTarget() {
  if (!app.session.user || !app.current) return null;
  const name = app.current.get('routeName');
  if (name === 'ppk2.profile') return app.route('index');
  if (!SETTINGS_SUBPAGES.includes(name)) return null;
  // «Безопасность и удаление» of another user (admins) keeps the usual back button
  if (name === 'user.security') {
    const user = app.current.get('user');
    if (user && user !== app.session.user) return null;
  }
  return app.route('ppk2.profile');
}
function showReaccept() {
  if (!app.session.user || !app.forum.attribute('ppk2AgreementPending')) return;
  if (app.current && app.current.get('routeName') === 'user.security') return;
  if (app.modal.modal && app.modal.modal.componentClass === ReacceptModal) return;
  app.modal.show(ReacceptModal, {
    everAccepted: app.forum.attribute('ppk2AgreementEverAccepted')
  });
}

/** v17: the cookie «locale» (the guest language; also read by the server before the browser language). */
function setLocaleCookie(locale) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = 'locale=' + locale + '; path=/; max-age=' + 5 * 365 * 24 * 3600 + '; SameSite=Lax' + secure;
}

/** v17: current URL without ?lang= (the server has already applied it and set the cookie). */
function urlWithoutLang() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has('lang')) return null;
  url.searchParams.delete('lang');
  return url.pathname + (url.search || '') + url.hash;
}
app.initializers.add('ppk2-profile', () => {
  // v17 language domains (variant B): after https://ppk2.org/...?lang=ru the address bar is
  // cleaned, so a later reload / language switch does not apply ?lang again.
  const clean = urlWithoutLang();
  if (clean !== null && window.history && window.history.replaceState) window.history.replaceState(window.history.state, '', clean);

  // v17 language switcher: writes BOTH the profile language (logged-in) and the cookie.
  override(HeaderSecondary.prototype, 'changeLocale', function (original, locale) {
    setLocaleCookie(locale);
    if (app.session.user) {
      app.session.user.savePreferences({
        locale
      }).then(() => window.location.reload());
    } else {
      window.location.reload();
    }
  });

  // v17 header: «Обращения» icon with the number of open requests (admins, «Техподдержка»),
  // next to the notifications bell.
  extend(HeaderSecondary.prototype, 'items', function (items) {
    if (!app.session.user || !app.forum.attribute('ppk2CanViewAccountLog')) return;
    const open = app.forum.attribute('ppk2SupportOpen') || 0;
    const label = extractText(t('header.support_tip', {
      count: open
    }));
    items.add('ppk2Support', m(LinkButton, {
      className: 'Button Button--flat Button--icon Ppk2HeaderSupport' + (open > 0 ? ' Ppk2HeaderSupport--open' : ''),
      href: app.route('ppk2.supportRequests'),
      icon: "fas fa-headset",
      title: label,
      "aria-label": label
    }, open > 0 ? m("span", {
      className: "Ppk2HeaderSupport-count"
    }, open > 99 ? '99+' : open) : null), 15);
  });

  // v17 «Показать пароль» in the log-in window
  extend('flarum/forum/components/LogInModal', 'fields', function (items) {
    if (!items.has('password')) return;
    const label = app.translator.trans('core.forum.log_in.password_placeholder');
    items.setContent('password', m("div", {
      className: "Form-group"
    }, withEye(m("input", {
      className: "FormControl",
      name: "password",
      type: "password",
      autocomplete: "current-password",
      placeholder: label,
      "aria-label": label,
      bidi: this.password,
      disabled: this.loading
    }))));
  });

  // Registration form: our fields, rules and the agreement checkbox
  extendSignUp();
  // staff «Редактировать»: login / password rules as hints
  extendEditUserHints();

  // Own user page: no «Управление» dropdown (all actions are in the left menu);
  // other users' pages keep it (admins, moderators).
  extend('flarum/forum/components/UserCard', 'controlsItems', function (items) {
    const user = this.attrs.user;
    if (user && app.session.user === user && String(this.attrs.className || '').includes('UserHero') && items.has('controls')) {
      items.remove('controls');
    }
  });

  // «Пользовательское соглашение» changed (or switched on) since the player's last acceptance:
  // the window «Принимаю» / «Покинуть портал» on every page except «Безопасность и удаление».
  extend('flarum/common/components/Page', 'oninit', function () {
    setTimeout(showReaccept, 0);
  });

  // «‹» on the settings pages (see SETTINGS_SUBPAGES): a fixed target instead of the history
  extend(Navigation.prototype, 'items', function (items) {
    const href = settingsBackTarget();
    if (!href) return;
    const toForum = href === app.route('index');
    const label = toForum ? app.translator.trans('core.forum.header.back_to_index_tooltip') : app.translator.trans('core.forum.header.settings_button');
    const back = m(LinkButton, {
      className: "Button Navigation-back Button--icon Ppk2SettingsBack",
      href: href,
      icon: "fas fa-chevron-left",
      "aria-label": label,
      title: label,
      onclick: e => {
        if (e.shiftKey || e.ctrlKey || e.metaKey || e.which === 2) return;
        e.preventDefault();
        m.route.set(href);
      }
    });
    if (items.has('back')) items.setContent('back', back);else items.add('back', back, 90);
  });

  // The standard settings page is fully replaced: /settings -> /my-profile. Its parts live in
  // «Главная» (password, email, privacy, vibration, items of other extensions), «Оповещения»,
  // «Цветовая схема» and «Безопасность и удаление».
  app.routes.settings = {
    path: '/settings',
    resolver: settingsRedirect
  };

  // Header avatar menu: «Настройки» (gear) opens our /my-profile.
  extend(SessionDropdown.prototype, 'items', function (items) {
    if (!app.session.user || !items.has('settings')) return;
    items.setContent('settings', m(LinkButton, {
      icon: "fas fa-cog",
      href: app.route('ppk2.profile')
    }, app.translator.trans('core.forum.header.settings_button')));
  });

  // Left menu of the own user pages:
  //   «Настройки» -> /my-profile; «Безопасность» -> «Безопасность и удаление»;
  //   after a spacer «Цветовая схема» (/color-scheme).
  // v18 public profile: «Профиль» (/u/{username}) and «Личная информация» (/u/{username}/info)
  // at the top of the left menu of every user page, then a line, then Посты / Обсуждения / …
  extend(UserPage.prototype, 'navItems', function (items) {
    if (!this.user) return;
    const username = this.user.slug();
    items.add('ppk2Profile', m(LinkButton, {
      href: app.route('user', {
        username
      }),
      icon: "fas fa-id-card"
    }, t('public.nav_profile')), 130);
    items.add('ppk2Info', m(LinkButton, {
      href: app.route('ppk2.userInfo', {
        username
      }),
      icon: "fas fa-user"
    }, t('public.nav_info')), 120);
    items.add('ppk2PublicSeparator', m(Separator, null), 110);
  });
  extend(UserPage.prototype, 'navItems', function (items) {
    const isActor = app.session.user && app.session.user === this.user;
    if (!isActor) return;
    if (items.has('settings')) {
      items.setContent('settings', m(LinkButton, {
        href: app.route('ppk2.profile'),
        icon: "fas fa-cog"
      }, app.translator.trans('core.forum.user.settings_link')));
    }
    if (items.has('security')) {
      items.setContent('security', m(LinkButton, {
        href: app.route('user.security', {
          username: this.user.slug()
        }),
        icon: "fas fa-shield-alt"
      }, t('nav.security')));
    }
    if (app.forum.attribute('ppk2CanViewAccountLog')) {
      items.add('ppk2AccountLog', m(LinkButton, {
        href: app.route('ppk2.accountLog'),
        icon: "fas fa-clipboard-list"
      }, app.translator.trans('ppk2-profile.lib.account_log.log_title')), -105);
    }
    if (app.forum.attribute('ppk2CanViewAccountLog')) {
      items.add('ppk2AgreementSettings', m(LinkButton, {
        href: app.route('ppk2.agreementSettings'),
        icon: "fas fa-file-signature"
      }, t('nav.agreement_settings')), -106);
      // «Обращения» with the number of open requests
      const open = app.forum.attribute('ppk2SupportOpen') || 0;
      items.add('ppk2SupportRequests', m(LinkButton, {
        href: app.route('ppk2.supportRequests'),
        icon: "fas fa-headset",
        className: "Ppk2NavSupport"
      }, t('nav.support_requests'), open > 0 ? m("span", {
        className: "Ppk2NavCount",
        title: String(open)
      }, open) : null), -107);
    }
    if (app.allowUserColorScheme) {
      items.add('ppk2ColorSchemeSeparator', m(Separator, null), -110);
      items.add('ppk2ColorScheme', m(LinkButton, {
        href: app.route('ppk2.colorScheme'),
        icon: "fas fa-palette"
      }, t('color_scheme.title')), -120);
    }
  });

  // «Безопасность и удаление»: «Экспорт данных» and «Удалить аккаунт» (flarum/gdpr modals),
  // own account only, only when flarum/gdpr is enabled.
  extend('flarum/forum/components/UserSecurityPage', 'settingsItems', function (items) {
    const user = this.user;
    if (!user || app.session.user !== user) return;
    const Erasure = gdpr('forum/components/RequestErasureModal');
    const Export = gdpr('common/components/RequestDataExportModal');
    if (!Erasure && !Export) return;
    items.add('ppk2Data', m(FieldSet, {
      className: "Ppk2SecurityData FieldSet--col",
      label: t('security.data_heading')
    }, Export && m("div", {
      className: "Form-group Ppk2SecurityData-export"
    }, m("p", {
      className: "helpText"
    }, t('security.export_help')), m(Button, {
      className: "Button",
      icon: "fas fa-file-export",
      onclick: () => app.modal.show(Export, {
        user
      })
    }, t('security.export_button'))), Erasure && m("div", {
      className: "Form-group Ppk2SecurityData-erase"
    }, m("p", {
      className: "helpText"
    }, t('security.erase_help')), m(Button, {
      className: "Button Button--danger",
      icon: "fas fa-user-minus",
      onclick: () => app.modal.show(Erasure)
    }, t('security.erase_button')))), -200);
  });
});
export { ProfileModal, ProfilePage, ColorSchemePage, AgreementModal, AvatarGalleryModal };